import { useState, useCallback, useEffect } from 'react'

import { useActionSheet } from '@expo/react-native-action-sheet'
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { Asset } from 'react-native-image-picker'
import { useFilePicker } from 'use-file-picker'

import { MediaItem } from '../types/media-types'
import {
  MainStackParamsType,
  MainStackNavigationType,
} from '../types/navigation-types'

export function usePickImageActionSheet(key: string) {
  const { t } = useTranslation()
  const { showActionSheetWithOptions } = useActionSheet()
  const { navigate } = useNavigation<MainStackNavigationType<'Root'>>()
  const { params, key: routeKey } =
    useRoute<RouteProp<MainStackParamsType, 'Root'>>()

  const [image, setImage] = useState<MediaItem>()

  const addFromRelefantLibrary = useCallback(() => {
    navigate('MediaLibraryPicker', {
      routeKey,
      singlePictureKey: key,
      availableFilters: ['picture'],
    })
  }, [navigate, key, routeKey])

  useEffect(() => {
    if (params?.[key]) {
      const libraryItem: MediaItem = params[key]
      setImage(libraryItem)
    }
  }, [params, key])

  const { openFilePicker, loading } = useFilePicker({
    multiple: false,
    readAs: 'DataURL',
    accept: ['image/*'],
    onFilesSuccessfullySelected(fileData) {
      const assets: Asset[] = []
      fileData.plainFiles.forEach((file, index) => {
        assets.push({
          uri: fileData.filesContent[index].content,
          fileSize: file.size,
          type: file.type,
          fileName: file.name,
        })
      })

      const asset = fileData.plainFiles[0]
      const uri = fileData.filesContent[0].content

      asset &&
        setImage({
          __typename: 'NewAttachment',
          url: uri,
          id: uri ?? '',
          signedBlobId: null,
          fileName: asset.name,
          contentType: asset.type,
        })
    },
  })

  const openImagePicker = useCallback(() => {
    const actions = ['chooseFromLibrary', 'chooseUsed', 'cancel'] as const

    showActionSheetWithOptions(
      {
        options: actions.map(action =>
          t(
            action === 'cancel'
              ? 'common.cancel'
              : `screens.createMemory.addMediaOptions.${action}`,
          ),
        ),
        cancelButtonIndex: actions.indexOf('cancel'),
      },
      buttonIndex => {
        const action = buttonIndex !== undefined && actions[buttonIndex]
        if (!action || action === 'cancel') return

        switch (action) {
          case 'chooseFromLibrary':
            openFilePicker()
            break
          case 'chooseUsed':
            addFromRelefantLibrary()
            break
        }
      },
    )
  }, [t, openFilePicker, addFromRelefantLibrary, showActionSheetWithOptions])

  const clear = useCallback(() => setImage(undefined), [])

  return { openImagePicker, image, isLoading: loading, clear }
}
